import Input from 'components/Input';
import Select from 'components/Select';
import React, { useState } from 'react';
import { Button, Modal } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createDepartment } from 'services/departments';
import { getWorkplaces } from 'services/workplaces';

const initialForm = {
    name: '',
    workplaceId: '',
};

function AddDepartmentModal({ open, onClose }) {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const queryClient = useQueryClient();

    const { data: workplacesRaw = [] } = useQuery({
        queryKey: ['workplaces'],
        queryFn: getWorkplaces,
        enabled: open,
    });

    const workplaceOptions = workplacesRaw.map(w => ({ value: w.id, label: w.name }));

    const mutation = useMutation({
        mutationFn: createDepartment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['departments'] });
            setForm(initialForm);
            setErrors({});
            onClose();
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = 'Department name is required.';
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        mutation.mutate({
            name: form.name,
            workplaceId: form.workplaceId || undefined,
            active: true,
        });
    };

    const handleClose = () => {
        setForm(initialForm);
        setErrors({});
        mutation.reset();
        onClose();
    };

    return (
        <Modal className='add-workplace-modal' show={open} onHide={handleClose} centered>
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
                <h4 className="sub-title mb-0">Add Department</h4>
                <button
                    type="button"
                    className="close bg-transparent border-0"
                    onClick={handleClose}
                >
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <Modal.Body className='pt-0'>
                <form onSubmit={handleSubmit}>
                    <div className='row mt-3'>
                        <div className='col-12'>
                            <label>Department Name*</label>
                            <Input
                                fullWidth
                                variant='flat'
                                placeholder='Enter Department Name'
                                name='name'
                                value={form.name}
                                onChange={handleChange}
                                error={!!errors.name}
                                errorText={errors.name}
                            />
                        </div>
                        <div className='col-12 mt-3'>
                            <label>Workplace</label>
                            <Select
                                fullWidth
                                placeholder='Select Workplace'
                                options={workplaceOptions}
                                name='workplaceId'
                                value={form.workplaceId}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    {mutation.isError && (
                        <p className="text-danger mt-3">
                            {mutation.error?.response?.data?.message || 'Failed to add department. Please try again.'}
                        </p>
                    )}
                    <Modal.Footer className='border-0 justify-content-start px-0 mb-5'>
                        <Button
                            type='submit'
                            className='button-orange'
                            disabled={mutation.isPending}
                        >
                            {mutation.isPending ? 'Saving...' : 'Add Department'}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal.Body>
        </Modal>
    );
}

export default AddDepartmentModal;
